import {
    ArticleDto,
    DimensionType,
    GoodsDto,
    GoodsStatus,
    OrderGoodsItemDto,
    OrderStatus,
    SiteType,
} from '../../api/ClientApi';
import { CompareTwoDateTimes, CompareTwoDates, FormatDateText } from '../../common/datesHelper';

export const getColorForGoodsStatus = (goodsStatus: GoodsStatus) => {
    switch (goodsStatus) {
        case GoodsStatus.InStock:
            return 'primary';
        case GoodsStatus.Sold:
            return 'success';
        case GoodsStatus.Used:
            return 'warning';
        case GoodsStatus.WrittenOff:
            return 'error';
        case GoodsStatus.InTransport:
            return 'secondary';
        default:
            return 'error';
    }
};

export type GoodsTableArrivalInfo = {
    batchId?: number;
    goodsQuantity?: number;
    batchPrice?: number;
    batchClient?: string;
    batchArrivalTime?: Date;
    batchUniqueId?: string;
    batchStatus?: GoodsStatus;
    siteName?: string;
    isReserved?: boolean;
};

export type GoodsTableExitInfo = {
    goodsQuantity?: number;
    sellingPrice?: number;
    soldToClient?: string;
    situation?: string;
    reason?: string;
    executionDate?: Date;
    goodsStatus?: GoodsStatus;
    siteName?: string;
    isReserved?: boolean;
};

export type GoodsTableBasicInfo = {
    goodsStatus?: GoodsStatus;
    goodsQuantity?: number;
    siteName?: string;
    isReserved?: boolean;
};

export function createGoodsData(
    articleId?: number,
    manufacturer?: string,
    title?: string,
    dimensionType?: DimensionType,
    innerCode?: string,
    outterCode?: string,
    labels?: string[],
    totalQuantity?: number,
    inStockQuantity?: number,
    averagePrice?: number,
    goodsArrivalInfo?: GoodsTableArrivalInfo[],
    goodsExitInfo?: GoodsTableExitInfo[],
    goodsBasicInfo?: GoodsTableBasicInfo[],
) {
    return {
        articleId,
        manufacturer,
        title,
        dimensionType,
        innerCode,
        outterCode,
        labels,
        totalQuantity,
        inStockQuantity,
        averagePrice,
        goodsArrivalInfo,
        goodsExitInfo,
        goodsBasicInfo,
    };
}

// export type GoodsStatusFilterType = {
//     InStock: boolean;
//     WrittenOff: boolean;
//     Used: boolean;
//     Sold: boolean;
// };

export const currGoodsTableRows = (
    includeSiteInfo: boolean,
    tableDetailsType: TableDetailsType | null,
    goods: GoodsDto[] | null,
    approvedOrderGoodsItems: OrderGoodsItemDto[] | null,
    articles: ArticleDto[] | null,
    articleFilter: string,
    labelFilter: string,
    situationFilter: number,
    goodsStatusFilter: GoodsStatus,
    uniqueBatchIdFilter: string,
    batchSupplierFilter: string,
    siteNameFilter: string,
    siteTypeFilter: SiteType | undefined,
    arrivalStartTime?: Date,
    arrivalEndTime?: Date,
    disableStatusFilter?: boolean,
) => {
    if (!goods) {
        return [];
    }

    const uniqueArticlesGoods = goods.filter(
        (value, index, array) => array.findIndex((g) => g.article?.id === value.article?.id) === index,
    );

    return uniqueArticlesGoods
        .map((ua) => {
            const articleFilterTokens = articleFilter
                .toLowerCase()
                .split(' ')
                .filter((a) => a !== '');

            const article = articles?.find((a) => {
                let articleValues = [
                    a.title?.toLowerCase() ?? [],
                    a.innerCode?.toLowerCase() ?? [],
                    a.outterCode?.toLowerCase() ?? [],
                    a.manufacturer?.toLowerCase() ?? [],
                ].flat();
                let numOfFinds = 0;

                articleFilterTokens.forEach((st) => {
                    const tokenIndex = articleValues.findIndex((t) => t.includes(st));

                    if (tokenIndex !== undefined && tokenIndex >= 0 && articleValues) {
                        articleValues = [
                            ...articleValues?.slice(0, tokenIndex),
                            ...articleValues.slice(tokenIndex + 1, articleValues?.length),
                        ];
                        numOfFinds++;
                    }
                });
                return (
                    ua.article?.id === a.id &&
                    numOfFinds === articleFilterTokens.length &&
                    (!!a.labels?.find((l) => l.title?.toLowerCase().includes(labelFilter.toLowerCase())) ||
                        !labelFilter ||
                        labelFilter.trim() === '')
                );
            });

            if (!article) {
                return [];
            }

            const { goodsArrivalInfo, totalArrivalGoodsQuantity, reservedArrivalGoodsQuantity } = getGoodsArrivalInfo(
                goods,
                article,
                goodsStatusFilter,
                batchSupplierFilter,
                uniqueBatchIdFilter,
                includeSiteInfo,
                siteNameFilter,
                siteTypeFilter,
                arrivalStartTime,
                arrivalEndTime,
                approvedOrderGoodsItems ?? undefined,
                disableStatusFilter,
            );

            const { goodsBasicInfo, totalBasicGoodsQuantity, reservedBasicGoodsQuantity } = getGoodsBasicInfo(
                goods,
                article,
                includeSiteInfo,
                '',
                undefined,
                approvedOrderGoodsItems ?? undefined,
            );

            const { goodsExitInfo, totalExitGoodsQuantity, reservedExitGoodsQuantity } = getGoodsExitInfo(
                goods,
                article,
                goodsStatusFilter,
                situationFilter,
                includeSiteInfo,
                siteNameFilter,
                siteTypeFilter,
                approvedOrderGoodsItems ?? undefined,
                disableStatusFilter,
            );

            const currGoodsTotalQuantity =
                tableDetailsType === TableDetailsType.Basic
                    ? totalBasicGoodsQuantity
                    : tableDetailsType === TableDetailsType.Arrival
                      ? totalArrivalGoodsQuantity
                      : tableDetailsType === TableDetailsType.Exit
                        ? totalExitGoodsQuantity
                        : 0;

            const currGoodsInStock = goods
                .filter((g) => g.article?.id === article.id && g.goodsStatus === GoodsStatus.InStock)
                .reduce((acc, val) => {
                    return (acc += val.quantity ?? 0);
                }, 0);

            const currGoodsInStockQuantity =
                tableDetailsType === TableDetailsType.Basic
                    ? currGoodsInStock - reservedBasicGoodsQuantity
                    : tableDetailsType === TableDetailsType.Arrival
                      ? currGoodsInStock - reservedArrivalGoodsQuantity
                      : tableDetailsType === TableDetailsType.Exit
                        ? currGoodsInStock - reservedExitGoodsQuantity
                        : 0;

            const pricedQuantity = goods
                .filter((g) => g.article?.id === article.id && g.goodsStatus === GoodsStatus.InStock)
                ?.reduce((acc, val) => {
                    return (acc += !!val.batch?.purchasePricePerArticle ? (val.quantity ?? 0) : 0);
                }, 0);

            const totalPrice = goods
                .filter((g) => g.article?.id === article.id && g.goodsStatus === GoodsStatus.InStock)
                ?.reduce((acc, val) => {
                    return (acc += (val.batch?.purchasePricePerArticle ?? 0) * (val.quantity ?? 0));
                }, 0);

            const averagePrice =
                Math.round(10 * (!!totalPrice && !!pricedQuantity ? totalPrice / pricedQuantity : 0)) / 10;

            const row = createGoodsData(
                article.id,
                article.manufacturer,
                article.title,
                article.dimensionType,
                article.innerCode,
                article.outterCode,
                article.labels?.map((l) => l.title ?? []).flat() ?? [],
                currGoodsTotalQuantity,
                currGoodsInStockQuantity,
                averagePrice,
                goodsArrivalInfo,
                goodsExitInfo,
                goodsBasicInfo,
            );

            if (shouldBeVisible(tableDetailsType, goodsBasicInfo, goodsExitInfo, goodsArrivalInfo)) {
                return [row];
            }

            return [];
        })
        ?.flat()
        .filter((ua) => !!ua.totalQuantity && ua.totalQuantity > 0);
};

export const filterUniqueGoodsByCriteriaForArrival = (currGoods: GoodsDto[]): GoodsDto[] => {
    const alteredGoods = currGoods.map((cg) => {
        return {
            id: cg.id,
            quantity: cg.quantity,
            key:
                (cg.article?.id ?? 0).toString() +
                '|' +
                (cg.batch?.id ?? 0).toString() +
                '|' +
                (!!cg.goodsStatus ? GoodsStatus[cg.goodsStatus] : '') +
                '|' +
                (cg.siteId ?? 0).toString(),
        };
    });

    const uniqueAlteredGoods = alteredGoods.filter(
        (value, index, array) => array.findIndex((g) => g.key === value.key) === index,
    );

    const goodsToReturn = uniqueAlteredGoods.map((uag) => {
        const allGoodsQuantity = alteredGoods
            .filter((ag) => ag.key === uag.key)
            .reduce((acc, val) => {
                return (acc += val.quantity ?? 0);
            }, 0);
        const oldGood = currGoods.find((cg) => cg.id === uag.id);

        const newGood = new GoodsDto(oldGood);
        newGood.quantity = allGoodsQuantity;

        return newGood;
    });

    return goodsToReturn;
};

export const squashUniqueOGIsByCriteria = (currOgis: OrderGoodsItemDto[]): OrderGoodsItemDto[] => {
    const alteredOGIs = currOgis.map((cg) => {
        return {
            id: cg.id,
            quantity: cg.quantity,
            articleId: cg.article?.id,
            siteId: cg.order?.originSiteId,
            key: (cg.article?.id ?? 0).toString() + '|' + (cg.order?.originSiteId ?? 0).toString(),
        };
    });

    const uniqueAlteredOGIs = alteredOGIs.filter(
        (value, index, array) => array.findIndex((g) => g.key === value.key) === index,
    );

    const OGIsToReturn = uniqueAlteredOGIs.map((uag) => {
        const allOGIsQuantity = alteredOGIs
            .filter((ag) => ag.key === uag.key)
            .reduce((acc, val) => {
                return (acc += val.quantity ?? 0);
            }, 0);
        const oldOgi = currOgis.find((cg) => cg.id === uag.id);

        const newOgi = new OrderGoodsItemDto(oldOgi);
        newOgi.quantity = allOGIsQuantity;

        return newOgi;
    });

    return OGIsToReturn;
};

export const squashUniqueGoodsByCriteriaForBasicInfo = (currGoods: GoodsDto[]): GoodsDto[] => {
    const alteredGoods = currGoods.map((cg) => {
        return {
            id: cg.id,
            quantity: cg.quantity,
            key:
                (cg.article?.id ?? 0).toString() +
                '|' +
                (!!cg.goodsStatus ? GoodsStatus[cg.goodsStatus] : '') +
                '|' +
                (cg.siteId ?? 0).toString(),
        };
    });

    const uniqueAlteredGoods = alteredGoods.filter(
        (value, index, array) => array.findIndex((g) => g.key === value.key) === index,
    );

    const goodsToReturn = uniqueAlteredGoods.map((uag) => {
        const allGoodsQuantity = alteredGoods
            .filter((ag) => ag.key === uag.key)
            .reduce((acc, val) => {
                return (acc += val.quantity ?? 0);
            }, 0);
        const oldGood = currGoods.find((cg) => cg.id === uag.id);

        const newGood = new GoodsDto(oldGood);
        newGood.quantity = allGoodsQuantity;

        return newGood;
    });

    return goodsToReturn;
};

export const squashUniqueGoodsByCriteriaForExit = (currGoods: GoodsDto[]): GoodsDto[] => {
    const alteredGoods = currGoods.map((cg) => {
        return {
            id: cg.id,
            quantity: cg.quantity,
            key:
                (cg.article?.id ?? 0).toString() +
                '|' +
                (!!cg.goodsStatus ? GoodsStatus[cg.goodsStatus] : '') +
                '|' +
                (cg.siteId ?? 0).toString() +
                '|' +
                (cg.goodsStatus === GoodsStatus.Sold
                    ? cg.exitInfo?.soldToId
                    : cg.goodsStatus === GoodsStatus.Used
                      ? cg.exitInfo?.situationId
                      : cg.goodsStatus === GoodsStatus.WrittenOff
                        ? cg.exitInfo?.orderId
                        : '') +
                (cg.goodsStatus !== GoodsStatus.InStock
                    ? '|' + FormatDateText(cg.exitInfo?.executionTime ?? new Date())
                    : ''),
        };
    });

    const uniqueAlteredGoods = alteredGoods.filter(
        (value, index, array) => array.findIndex((g) => g.key === value.key) === index,
    );

    const goodsToReturn = uniqueAlteredGoods.map((uag) => {
        const allGoodsQuantity = alteredGoods
            .filter((ag) => ag.key === uag.key)
            .reduce((acc, val) => {
                return (acc += val.quantity ?? 0);
            }, 0);
        const oldGood = currGoods.find((cg) => cg.id === uag.id);

        const newGood = new GoodsDto(oldGood);
        newGood.quantity = allGoodsQuantity;

        return newGood;
    });

    return goodsToReturn;
};

const getGoodsArrivalInfo = (
    goods: GoodsDto[],
    article: ArticleDto,
    goodsStatusFilter: GoodsStatus,
    batchSupplierFilter: string,
    uniqueBatchIdFilter: string,
    includeSiteInfo: boolean,
    siteNameFilter: string,
    siteTypeFilter?: SiteType,
    arrivalStartTime?: Date,
    arrivalEndTime?: Date,
    approvedOrderGoodsItems?: OrderGoodsItemDto[],
    disableStatusFilter?: boolean,
): {
    goodsArrivalInfo: GoodsTableArrivalInfo[];
    totalArrivalGoodsQuantity: number;
    reservedArrivalGoodsQuantity: number;
} => {
    const currGoods = goods
        .filter((g) => g.article?.id === article.id)
        .filter((g) => {
            if (!g.goodsStatus) {
                return true;
            }

            return disableStatusFilter || g.goodsStatus === goodsStatusFilter;
        })
        .filter((g) => {
            return (
                g.batch?.uniqueBatchId?.toLowerCase().includes(uniqueBatchIdFilter.toLowerCase()) &&
                (!batchSupplierFilter ||
                    batchSupplierFilter === '' ||
                    g.batch?.supplier?.name?.toLowerCase().includes(batchSupplierFilter.toLowerCase()) ||
                    g.batch?.supplier?.shortName?.toLowerCase().includes(batchSupplierFilter.toLowerCase())) &&
                (!includeSiteInfo ||
                    (g.site?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                        (g.site.siteType === siteTypeFilter || !siteTypeFilter)))
            );
        })
        .filter((g) => {
            let retVal = true;

            if (!g.batch?.batchArrivalTime) {
                return true;
            }

            if (!!arrivalStartTime) {
                retVal = CompareTwoDates(arrivalStartTime, g.batch?.batchArrivalTime);
            }

            if (!!arrivalEndTime) {
                retVal &&= CompareTwoDates(g.batch?.batchArrivalTime, arrivalEndTime);
            }

            return retVal;
        });

    const currOrderedGoodsItems = !approvedOrderGoodsItems
        ? []
        : approvedOrderGoodsItems
              .filter((ogi) => ogi.article?.id === article.id)
              .filter((ogi) => {
                  return (
                      (!batchSupplierFilter ||
                          batchSupplierFilter === '' ||
                          ogi.order?.supplier?.name?.toLowerCase().includes(batchSupplierFilter.toLowerCase()) ||
                          ogi.order?.supplier?.shortName?.toLowerCase().includes(batchSupplierFilter.toLowerCase())) &&
                      (!includeSiteInfo ||
                          (ogi.order?.originSite?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                              (ogi.order.originSite.siteType === siteTypeFilter || !siteTypeFilter)))
                  );
              });

    const totalArrivalGoodsQuantity = currGoods
        .filter((g) => disableStatusFilter || g.goodsStatus === goodsStatusFilter)
        .reduce((acc, val) => {
            return (acc += val.quantity ?? 0);
        }, 0);

    const reservedArrivalGoodsQuantity = currOrderedGoodsItems.reduce((acc, val) => {
        return (acc += val.quantity ?? 0);
    }, 0);

    const filteredGoods = filterUniqueGoodsByCriteriaForArrival(currGoods)?.sort((a, b) => {
        const aDate = a.batch?.batchArrivalTime ?? new Date();
        const bDate = b.batch?.batchArrivalTime ?? new Date();

        return CompareTwoDateTimes(bDate, aDate, false) ? 1 : -1;
    });

    // filter ogis like goods and use them instead of "ogis", and change them instead of "ogis"
    // remember to do this again when using these ogis next time

    let filteredOgis = squashUniqueOGIsByCriteria(currOrderedGoodsItems);

    let goodsInfo = filteredGoods
        ?.map((gf) => {
            const batch = gf.batch;

            const currOgi = filteredOgis.find(
                (ogi) => ogi.articleId === gf.article?.id && ogi.order?.originSiteId === gf.siteId,
            );

            if (
                !!currOgi &&
                !!currOgi.quantity &&
                currOgi.quantity > 0 &&
                !!gf.quantity &&
                gf.goodsStatus === GoodsStatus.InStock
            ) {
                if (currOgi.quantity >= gf.quantity) {
                    currOgi.quantity -= gf.quantity;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return {
                        batchArrivalTime: batch?.batchArrivalTime,
                        batchClient: batch?.supplier?.shortName ?? batch?.supplier?.name,
                        batchPrice: batch?.purchasePricePerArticle,
                        batchStatus: gf.goodsStatus,
                        batchUniqueId: batch?.uniqueBatchId,
                        batchId: batch?.id,
                        goodsQuantity: gf.quantity,
                        siteName: gf.site?.name,
                        isReserved: true,
                    } as GoodsTableArrivalInfo;
                } else {
                    const gti = {
                        batchArrivalTime: batch?.batchArrivalTime,
                        batchClient: batch?.supplier?.shortName ?? batch?.supplier?.name,
                        batchPrice: batch?.purchasePricePerArticle,
                        batchStatus: gf.goodsStatus,
                        batchUniqueId: batch?.uniqueBatchId,
                        batchId: batch?.id,
                        goodsQuantity: gf.quantity - currOgi.quantity,
                        siteName: gf.site?.name,
                        isReserved: false,
                    } as GoodsTableArrivalInfo;

                    const gtiReserverd = {
                        batchArrivalTime: batch?.batchArrivalTime,
                        batchClient: batch?.supplier?.shortName ?? batch?.supplier?.name,
                        batchPrice: batch?.purchasePricePerArticle,
                        batchStatus: gf.goodsStatus,
                        batchUniqueId: batch?.uniqueBatchId,
                        batchId: batch?.id,
                        goodsQuantity: currOgi.quantity,
                        siteName: gf.site?.name,
                        isReserved: true,
                    } as GoodsTableArrivalInfo;

                    currOgi.quantity = 0;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return [gti, gtiReserverd];
                }
            }

            return {
                batchArrivalTime: batch?.batchArrivalTime,
                batchClient: batch?.supplier?.shortName ?? batch?.supplier?.name,
                batchPrice: batch?.purchasePricePerArticle,
                batchStatus: gf.goodsStatus,
                batchUniqueId: batch?.uniqueBatchId,
                batchId: batch?.id,
                goodsQuantity: gf.quantity,
                siteName: gf.site?.name,
            } as GoodsTableArrivalInfo;
        })
        .flat()
        ?.sort((a, b) => {
            const aDate = a?.batchArrivalTime ?? new Date();
            const bDate = b?.batchArrivalTime ?? new Date();

            return CompareTwoDateTimes(aDate, bDate, false) ? -1 : 1;
        })
        .sort((a, b) => {
            return a.isReserved ? 1 : -1;
        });

    if (!disableStatusFilter && goodsStatusFilter !== GoodsStatus.InStock) {
        filteredOgis = squashUniqueOGIsByCriteria(currOrderedGoodsItems);

        const reservedGoodsInfo = filteredGoods
            ?.map((gf) => {
                const batch = gf.batch;

                const currOgi = filteredOgis.find(
                    (ogi) => ogi.articleId === gf.article?.id && ogi.order?.originSiteId === gf.siteId,
                );

                if (!!currOgi && !!currOgi.quantity && currOgi.quantity > 0) {
                    const gtai = {
                        batchArrivalTime: batch?.batchArrivalTime,
                        batchClient: batch?.supplier?.shortName ?? batch?.supplier?.name,
                        batchPrice: batch?.purchasePricePerArticle,
                        batchStatus: gf.goodsStatus,
                        batchUniqueId: batch?.uniqueBatchId,
                        batchId: batch?.id,
                        goodsQuantity: currOgi?.quantity,
                        siteName: gf.site?.name,
                        isReserved: true,
                    } as GoodsTableArrivalInfo;

                    currOgi.quantity = 0;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi?.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return gtai;
                }
                return [];
            })
            .flat()
            ?.sort((a, b) => {
                const aDate = a?.batchArrivalTime ?? new Date();
                const bDate = b?.batchArrivalTime ?? new Date();

                return CompareTwoDateTimes(aDate, bDate, false) ? -1 : 1;
            })
            .sort((a, b) => {
                return a.isReserved ? 1 : -1;
            });

        goodsInfo = goodsInfo.concat(reservedGoodsInfo);
    }

    goodsInfo = goodsInfo.filter(
        (a) =>
            disableStatusFilter ||
            (goodsStatusFilter !== GoodsStatus.InStock && !a.isReserved) ||
            goodsStatusFilter === GoodsStatus.InStock,
    );

    return { goodsArrivalInfo: goodsInfo, totalArrivalGoodsQuantity, reservedArrivalGoodsQuantity };
};

const getGoodsBasicInfo = (
    goods: GoodsDto[],
    article: ArticleDto,
    includeSiteInfo: boolean,
    siteNameFilter: string,
    siteTypeFilter?: SiteType,
    approvedOrderGoodsItems?: OrderGoodsItemDto[],
): { goodsBasicInfo: GoodsTableBasicInfo[]; totalBasicGoodsQuantity: number; reservedBasicGoodsQuantity: number } => {
    const currGoods = goods
        .filter((g) => g.article?.id === article.id)
        .filter((g) => {
            return (
                !includeSiteInfo ||
                (g.site?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                    (g.site.siteType === siteTypeFilter || !siteTypeFilter))
            );
        });

    const currOrderedGoodsItems = !approvedOrderGoodsItems
        ? []
        : approvedOrderGoodsItems
              .filter((ogi) => ogi.article?.id === article.id)
              .filter((ogi) => {
                  return (
                      !includeSiteInfo ||
                      (ogi.order?.originSite?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                          (ogi.order.originSite.siteType === siteTypeFilter || !siteTypeFilter))
                  );
              });

    const totalBasicGoodsQuantity = currGoods
        .filter((g) => g.goodsStatus === GoodsStatus.InStock)
        .reduce((acc, val) => {
            return (acc += val.quantity ?? 0);
        }, 0);

    const reservedBasicGoodsQuantity = currOrderedGoodsItems.reduce((acc, val) => {
        return (acc += val.quantity ?? 0);
    }, 0);

    const basicFilteredGoods = squashUniqueGoodsByCriteriaForBasicInfo(currGoods);

    let filteredOgis = squashUniqueOGIsByCriteria(currOrderedGoodsItems);

    const goodsBasicInfo = basicFilteredGoods
        ?.map((gf) => {
            const currOgi = filteredOgis.find(
                (ogi) => ogi.articleId === gf.article?.id && ogi.order?.originSiteId === gf.siteId,
            );

            if (
                !!currOgi &&
                !!currOgi.quantity &&
                currOgi.quantity > 0 &&
                !!gf.quantity &&
                gf.goodsStatus === GoodsStatus.InStock
            ) {
                if (currOgi.quantity >= gf.quantity) {
                    currOgi.quantity -= gf.quantity;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: gf.quantity,
                        siteName: gf.site?.name,
                        isReserved: true,
                    } as GoodsTableBasicInfo;
                } else {
                    const gti = {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: gf.quantity - currOgi.quantity,
                        siteName: gf.site?.name,
                        isReserved: false,
                    } as GoodsTableBasicInfo;

                    const gtiReserverd = {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: currOgi.quantity,
                        siteName: gf.site?.name,
                        isReserved: true,
                    } as GoodsTableBasicInfo;

                    currOgi.quantity = 0;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return [gti, gtiReserverd];
                }
            }

            return {
                goodsStatus: gf.goodsStatus,
                goodsQuantity: gf.quantity,
                siteName: gf.site?.name,
            } as GoodsTableBasicInfo;
        })
        .flat()
        .sort((a, b) => {
            return a.isReserved ? 1 : -1;
        });
    return { goodsBasicInfo, totalBasicGoodsQuantity, reservedBasicGoodsQuantity };
};

const getGoodsExitInfo = (
    goods: GoodsDto[],
    article: ArticleDto,
    goodsStatusFilter: GoodsStatus,
    situationFilter: number,
    includeSiteInfo: boolean,
    siteNameFilter: string,
    siteTypeFilter?: SiteType,
    approvedOrderGoodsItems?: OrderGoodsItemDto[],
    disableStatusFilter?: boolean,
): { goodsExitInfo: GoodsTableExitInfo[]; totalExitGoodsQuantity: number; reservedExitGoodsQuantity: number } => {
    const currGoodsWithoutStatusFilter = goods
        .filter((g) => g.article?.id === article.id)
        .filter((g) => {
            if (situationFilter > 0) {
                if (!!g.exitInfo?.situationId) {
                    return g.exitInfo.situationId === situationFilter;
                }

                return false;
            }

            return true;
        })
        .filter((g) => {
            return (
                !includeSiteInfo ||
                (g.site?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                    (g.site.siteType === siteTypeFilter || !siteTypeFilter))
            );
        });

    const currGoods = currGoodsWithoutStatusFilter.filter((g) => {
        if (!g.goodsStatus) {
            return true;
        }

        return disableStatusFilter || g.goodsStatus === goodsStatusFilter;
    });

    const currOrderedGoodsItems = !approvedOrderGoodsItems
        ? []
        : approvedOrderGoodsItems
              .filter((ogi) => ogi.article?.id === article.id)
              .filter((ogi) => {
                  return (
                      !includeSiteInfo ||
                      (ogi.order?.originSite?.name?.toLowerCase().includes(siteNameFilter.toLowerCase()) &&
                          (ogi.order.originSite.siteType === siteTypeFilter || !siteTypeFilter))
                  );
              });

    const totalExitGoodsQuantity = currGoods
        .filter((g) => disableStatusFilter || g.goodsStatus === goodsStatusFilter)
        .reduce((acc, val) => {
            return (acc += val.quantity ?? 0);
        }, 0);

    const reservedExitGoodsQuantity = currOrderedGoodsItems.reduce((acc, val) => {
        return (acc += val.quantity ?? 0);
    }, 0);

    const filteredExitGoods = squashUniqueGoodsByCriteriaForExit(currGoods);

    let filteredOgis = squashUniqueOGIsByCriteria(currOrderedGoodsItems);

    let goodsExitInfo = filteredExitGoods
        ?.map((gf) => {
            // gf === all goods with this article on this site

            // filter ogis to only those with same orderOriginSite as all of the currGoods that have same articleId as current gf
            // also take into consideration only orders with pending status

            const currOgi = filteredOgis.find(
                (ogi) => ogi.articleId === gf.article?.id && ogi.order?.originSiteId === gf.siteId,
            );

            if (
                !!currOgi &&
                !!currOgi.quantity &&
                currOgi.quantity > 0 &&
                !!gf.quantity &&
                gf.goodsStatus === GoodsStatus.InStock
            ) {
                if (currOgi.quantity >= gf.quantity) {
                    currOgi.quantity -= gf.quantity;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: gf.quantity,
                        siteName: gf.site?.name,
                        executionDate: gf.exitInfo?.executionTime,
                        reason: gf.exitInfo?.reason,
                        sellingPrice: gf.exitInfo?.sellingPrice,
                        situation: gf.exitInfo?.situation?.title,
                        soldToClient: gf.exitInfo?.soldTo?.shortName ?? gf.exitInfo?.soldTo?.name,
                        isReserved: true,
                    } as GoodsTableExitInfo;
                } else {
                    const gti = {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: gf.quantity - currOgi.quantity,
                        siteName: gf.site?.name,
                        executionDate: gf.exitInfo?.executionTime,
                        reason: gf.exitInfo?.reason,
                        sellingPrice: gf.exitInfo?.sellingPrice,
                        situation: gf.exitInfo?.situation?.title,
                        soldToClient: gf.exitInfo?.soldTo?.shortName ?? gf.exitInfo?.soldTo?.name,
                        isReserved: false,
                    } as GoodsTableExitInfo;

                    const gtiReserverd = {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: currOgi.quantity,
                        siteName: gf.site?.name,
                        executionDate: gf.exitInfo?.executionTime,
                        reason: gf.exitInfo?.reason,
                        sellingPrice: gf.exitInfo?.sellingPrice,
                        situation: gf.exitInfo?.situation?.title,
                        soldToClient: gf.exitInfo?.soldTo?.shortName ?? gf.exitInfo?.soldTo?.name,
                        isReserved: true,
                    } as GoodsTableExitInfo;

                    currOgi.quantity = 0;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return [gti, gtiReserverd];
                }
            }

            return {
                goodsStatus: gf.goodsStatus,
                goodsQuantity: gf.quantity,
                siteName: gf.site?.name,
                executionDate: gf.exitInfo?.executionTime,
                reason: gf.exitInfo?.reason,
                sellingPrice: gf.exitInfo?.sellingPrice,
                situation: gf.exitInfo?.situation?.title,
                soldToClient: gf.exitInfo?.soldTo?.shortName ?? gf.exitInfo?.soldTo?.name,
            } as GoodsTableExitInfo;
        })
        .flat()
        .sort((a, b) => {
            return a.isReserved ? 1 : -1;
        });

    if (!disableStatusFilter && goodsStatusFilter !== GoodsStatus.InStock) {
        filteredOgis = squashUniqueOGIsByCriteria(currOrderedGoodsItems);

        const reservedGoodsInfo = filteredExitGoods
            ?.map((gf) => {
                const currOgi = filteredOgis.find(
                    (ogi) => ogi.articleId === gf.article?.id && ogi.order?.originSiteId === gf.siteId,
                );

                if (!!currOgi && !!currOgi.quantity && currOgi.quantity > 0) {
                    const gtei = {
                        goodsStatus: gf.goodsStatus,
                        goodsQuantity: currOgi?.quantity,
                        siteName: gf.site?.name,
                        executionDate: gf.exitInfo?.executionTime,
                        reason: gf.exitInfo?.reason,
                        sellingPrice: gf.exitInfo?.sellingPrice,
                        situation: gf.exitInfo?.situation?.title,
                        soldToClient: gf.exitInfo?.soldTo?.shortName ?? gf.exitInfo?.soldTo?.name,
                        isReserved: true,
                    } as GoodsTableExitInfo;

                    currOgi.quantity = 0;

                    const oldOgiIndex = filteredOgis.findIndex((ogi) => ogi.id === currOgi?.id);

                    filteredOgis = [
                        ...filteredOgis.slice(0, oldOgiIndex),
                        currOgi,
                        ...filteredOgis.slice(oldOgiIndex + 1),
                    ];

                    return gtei;
                }
                return [];
            })
            .flat();
        goodsExitInfo = goodsExitInfo.concat(reservedGoodsInfo);
    }

    goodsExitInfo = goodsExitInfo.filter(
        (a) =>
            disableStatusFilter ||
            (goodsStatusFilter !== GoodsStatus.InStock && !a.isReserved) ||
            goodsStatusFilter === GoodsStatus.InStock,
    );

    return { goodsExitInfo, totalExitGoodsQuantity, reservedExitGoodsQuantity };
};

const shouldBeVisible = (
    tableDetailsType: TableDetailsType | null,
    goodsBasicInfo?: GoodsTableBasicInfo[],
    goodsExitInfo?: GoodsTableExitInfo[],
    goodsArrivalInfo?: GoodsTableArrivalInfo[],
) => {
    return tableDetailsType === TableDetailsType.Basic
        ? !!goodsBasicInfo && goodsBasicInfo.length > 0
        : tableDetailsType === TableDetailsType.Exit
          ? !!goodsExitInfo && goodsExitInfo.length > 0
          : tableDetailsType === TableDetailsType.Arrival
            ? !!goodsArrivalInfo && goodsArrivalInfo.length > 0
            : false;
};

export enum TableDetailsType {
    Basic = 'Basic',
    Exit = 'Exit',
    Arrival = 'Arrival',
}

export enum TableDetailsTypeSR {
    Basic = 'Osnovni prikaz',
    Exit = 'Izlazni detalji',
    Arrival = 'Dolazni detalji',
}
